import useSWR from 'swr'
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { IconSend, IconChartSankey } from '@tabler/icons-react'
import {
  Grid,
  Typography,
  Box,
  Skeleton,
  Dialog,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { ComponentProps, useState } from 'react'
import DashboardMainInfoItem from '../../../Dashboard/components/DashboardMainInfoItem'
import DashboardDivider from '../../../Dashboard/components/DashboardDivider'
import OrderAnalyticsTable from '../../table/OrderAnalyticsTable'
import GoogleAnalyticsTable from '../../table/GoogleAnalyticsTable'
import SupplierLogo from '../../../../components/SupplierLogo'
import axiosInstance from '../../../../utils/axiosInstance'

interface IOrderAnalytics {
  orderId?: string
}

type Platform = 'total' | 'meta' | 'google'

interface IGoogleCampaign {
  impressions: number | null
  clicks: number | null
  conversions: number | null
  ads?: ComponentProps<typeof GoogleAnalyticsTable>['adsData']
}

const formatCompact = (value: number) =>
  value > 99999 ? `${value.toString().substring(0, 3)}K` : value.toLocaleString()

const formatDecimal = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined ? '0,00' : Number(value).toFixed(digits).replace('.', ',')

const OrderAnalytics = ({ orderId }: IOrderAnalytics) => {
  const { data, error, isLoading } = useSWR(
    orderId ? `/api/orders/${orderId}/analytics` : null,
    async (url) => {
      const response = await axiosInstance.get(url)
      return response.data
    }
  )

  const [platform, setPlatform] = useState<Platform>('meta')
  const [openPreview, setOpenPreview] = useState(false)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [previewAlt, setPreviewAlt] = useState<string>('Ad Preview')

  const handleOpenImage = (src: string | null, alt: string) => {
    if (!src) return
    setPreviewSrc(src)
    setPreviewAlt(alt)
    setOpenPreview(true)
  }

  const handleCloseImage = () => {
    setOpenPreview(false)
    setPreviewSrc(null)
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">No analytics data available</Typography>
      </Box>
    )
  }

  const originalMetaCampaign = data.originalMetaCampaign
  const metaCampaign = data.metaCampaign
  const googleCampaigns: IGoogleCampaign[] = data.googleCampaigns || []

  const hasMeta = Boolean(originalMetaCampaign || metaCampaign)
  const hasGoogle = googleCampaigns.length > 0

  if (!hasMeta && !hasGoogle) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">
          In order to view the Analytics, you need to connect the your order with the related
          Campaign in edit order.
        </Typography>
      </Box>
    )
  }

  // --- Meta metrics ---
  const metaClicks = originalMetaCampaign
    ? (originalMetaCampaign.maleClicks || 0) +
      (originalMetaCampaign.femaleClicks || 0) +
      (originalMetaCampaign.unknownClicks || 0)
    : 0

  const metaLeads = originalMetaCampaign
    ? (originalMetaCampaign.maleLeads || 0) +
      (originalMetaCampaign.femaleLeads || 0) +
      (originalMetaCampaign.unknownLeads || 0)
    : metaCampaign?.totalLeadsCount || 0

  const metaImpressions =
    originalMetaCampaign?.ads?.reduce(
      (sum: number, ad: { impressions?: number }) => sum + (ad.impressions || 0),
      0
    ) || 0

  const metaCtr = metaImpressions > 0 ? (metaClicks / metaImpressions) * 100 : 0

  const adsData = originalMetaCampaign?.ads || []

  // --- Google metrics ---
  // Unlike Meta, each Google campaign row already carries its aggregated totals, so the
  // only summing left is across the campaigns connected to the order. CTR is recomputed
  // from those sums for the same reason as the combined view below.
  const sumGoogle = (key: 'impressions' | 'clicks' | 'conversions') =>
    googleCampaigns.reduce((sum, campaign) => sum + (campaign[key] ?? 0), 0)
  const googleAds = googleCampaigns.flatMap((campaign) => campaign.ads || [])
  const googleImpressions = sumGoogle('impressions')
  const googleClicks = sumGoogle('clicks')
  const googleCtr = googleImpressions > 0 ? (googleClicks / googleImpressions) * 100 : 0
  const googleConversions = sumGoogle('conversions')

  // --- Combined ---
  // Only impressions and clicks are genuinely additive: both platforms report them in the
  // same unit. CTR is a ratio, so it is RECOMPUTED from the combined totals rather than
  // averaged — averaging two CTRs weights a tiny campaign the same as a huge one.
  //
  // Meta "leads" and Google "conversions" are deliberately NOT added together: a Meta lead
  // is a lead-form submission, while a Google conversion is whatever the account defines as
  // one (and can be fractional). They stay on separate tiles.
  const totalImpressions = metaImpressions + googleImpressions
  const totalClicks = metaClicks + googleClicks
  const totalCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0

  const bothConnected = hasMeta && hasGoogle

  // Meta is the default view. Derived rather than stored so it stays correct if the data
  // reloads, and so an order connected only to Google shows Google instead of empty Meta
  // tiles. 'total' is only reachable once both platforms are connected.
  const activePlatform: Platform = (() => {
    if (!bothConnected) return hasMeta ? 'meta' : 'google'
    return platform
  })()

  const dividerItem = (
    <Grid
      item
      xs={0.45}
      sx={{
        display: { xs: 'none', sm: 'none', md: 'block' },
      }}
    >
      <DashboardDivider />
    </Grid>
  )

  return (
    <Stack mb={4} p={1} spacing={10}>
      {/* Only offer the switch when both platforms are connected — a lone toggle is noise. */}
      {bothConnected && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={activePlatform}
          onChange={(_e, value: Platform | null) => {
            if (value) setPlatform(value)
          }}
          sx={{ alignSelf: 'flex-start' }}
        >
          <ToggleButton value="meta" sx={{ textTransform: 'none', px: 2 }}>
            <SupplierLogo supplier="meta" size={18} />
          </ToggleButton>
          <ToggleButton value="google" sx={{ textTransform: 'none', px: 2 }}>
            <SupplierLogo supplier="google" size={18} />
          </ToggleButton>
          <ToggleButton value="total" sx={{ textTransform: 'none', px: 2 }}>
            Total
          </ToggleButton>
        </ToggleButtonGroup>
      )}

      <Stack borderRadius={4} spacing={4} sx={{ backgroundColor: '#fff' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontSize={16} fontWeight={600} color="#1F0E72">
            Dashboard
          </Typography>
          {/* Name the platform the figures belong to, so a single-platform order is never
              ambiguous about whose numbers these are. */}
          {activePlatform !== 'total' && <SupplierLogo supplier={activePlatform} size={18} />}
        </Stack>

        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            gap: { xs: 2, md: 0 },
          }}
        >
          <Grid item md={2.3} xs={12}>
            <DashboardMainInfoItem
              isLoading={isLoading}
              title="Impressions"
              value={formatCompact(
                activePlatform === 'total'
                  ? totalImpressions
                  : activePlatform === 'meta'
                  ? metaImpressions
                  : googleImpressions
              )}
              Icon={<VisibilityOutlinedIcon htmlColor="#fff" fontSize="large" />}
            />
          </Grid>
          {dividerItem}
          <Grid item md={2.3} xs={12}>
            <DashboardMainInfoItem
              isLoading={isLoading}
              title="Clicks"
              value={(activePlatform === 'total'
                ? totalClicks
                : activePlatform === 'meta'
                ? metaClicks
                : googleClicks
              ).toLocaleString()}
              Icon={<IconSend fontSize="large" color="#fff" />}
            />
          </Grid>
          {dividerItem}
          <Grid item md={2.3} xs={12}>
            <DashboardMainInfoItem
              isLoading={isLoading}
              title="CTR"
              value={`${formatDecimal(
                activePlatform === 'total'
                  ? totalCtr
                  : activePlatform === 'meta'
                  ? metaCtr
                  : googleCtr
              )}%`}
              Icon={<IconChartSankey color="#fff" fontSize="large" />}
            />
          </Grid>
          {dividerItem}

          {/* Results tile. Meta reports leads, Google reports conversions — different
              definitions, so the combined view shows them side by side instead of summing. */}
          {activePlatform === 'meta' && (
            <Grid item md={2.3} xs={12}>
              <DashboardMainInfoItem
                isLoading={isLoading}
                title="Leads"
                value={metaLeads.toLocaleString()}
                Icon={<HowToRegOutlinedIcon htmlColor="#fff" fontSize="large" />}
              />
            </Grid>
          )}

          {activePlatform === 'google' && (
            <Grid item md={2.3} xs={12}>
              <DashboardMainInfoItem
                isLoading={isLoading}
                title="Conversions"
                value={formatDecimal(googleConversions)}
                Icon={<HowToRegOutlinedIcon htmlColor="#fff" fontSize="large" />}
              />
            </Grid>
          )}

          {activePlatform === 'total' && (
            <Grid item md={2.3} xs={12}>
              <DashboardMainInfoItem
                isLoading={isLoading}
                title="Meta Leads"
                value={metaLeads.toLocaleString()}
                Icon={<HowToRegOutlinedIcon htmlColor="#fff" fontSize="large" />}
              />
            </Grid>
          )}
        </Grid>

        {activePlatform === 'total' && (
          <Grid container alignItems="center" sx={{ gap: { xs: 2, md: 0 } }}>
            <Grid item md={2.3} xs={12}>
              <DashboardMainInfoItem
                isLoading={isLoading}
                title="Google Conversions"
                value={formatDecimal(googleConversions)}
                Icon={<HowToRegOutlinedIcon htmlColor="#fff" fontSize="large" />}
              />
            </Grid>
          </Grid>
        )}
      </Stack>

      {/* Ads performance. Each platform's ads table has its own columns and creative
          handling, so the combined view stacks both rather than merging rows. */}
      {(activePlatform === 'meta' || activePlatform === 'total') && adsData.length > 0 && (
        <Stack spacing={4}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography fontSize={16} fontWeight={600} color="#1F0E72">
              Ads Performance
            </Typography>
            <SupplierLogo supplier="meta" size={18} />
          </Stack>
          <OrderAnalyticsTable adsData={adsData} onOpenImage={handleOpenImage} />
        </Stack>
      )}

      {(activePlatform === 'google' || activePlatform === 'total') && googleAds.length > 0 && (
        <Stack spacing={4}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography fontSize={16} fontWeight={600} color="#1F0E72">
              Ads Performance
            </Typography>
            <SupplierLogo supplier="google" size={18} />
          </Stack>
          <GoogleAnalyticsTable adsData={googleAds} />
        </Stack>
      )}

      <Dialog
        sx={{
          '& .MuiDialog-container': {
            '& .MuiPaper-root': {
              padding: 0,
            },
          },
        }}
        open={Boolean(openPreview && previewSrc)}
        onClose={handleCloseImage}
      >
        <img
          src={previewSrc as string}
          alt={previewAlt}
          style={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'block',
            cursor: 'pointer',
            objectFit: 'contain',
          }}
        />
      </Dialog>
    </Stack>
  )
}

export default OrderAnalytics
